import numeral from 'numeral';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { NumberHelper } from 'core/helpers';
import { getPercentage } from 'core/utils';
import { CorporatePartCostStatus } from 'corporate/enums';
import { PriceCurrency } from 'pricing/enums';

const PricingHelper = {
  PRECISION: 0.00001,
  GPM_PRECISION: 0.0001,
  PRICING_TYPE_BASE: 'base',
  PRICING_TYPE_ADDER: 'adder',
  PRICING_TYPE_TOTAL: 'total',
  COMPARE_RESULT_EQUAL: 0,
  COMPARE_RESULT_GREATER: 1,
  COMPARE_RESULT_SMALLER: -1,
  DEFAULT_MAX_DECIMALS: 5,
  DEFAULT_COST_DECIMALS: 5,
  DEFAULT_PRICING_DECIMALS: 5,
  DEFAULT_GPM_DECIMALS: 4,
  DEFAULT_SMALL_PRICING_DECIMALS: 5,
  DEFAULT_BIG_PRICING_DECIMALS: 2,

  get: (pricingValue: any, placeholder = null): number | null =>
    PricingHelper.isNonEmpty(pricingValue) ? parseFloat(pricingValue) : placeholder,

  areEqual: (
    firstPrice: number,
    secondPrice: number,
    decimals = PricingHelper.DEFAULT_PRICING_DECIMALS
  ): boolean => NumberHelper.areEqual(firstPrice, secondPrice, decimals),

  areGpmsEqual: (firstGpm: number, secondGpm: number): boolean =>
    NumberHelper.areEqual(firstGpm, secondGpm, PricingHelper.DEFAULT_GPM_DECIMALS),

  computeGpm: (
    cost: string | number,
    price: string | number,
    opts: { decimals?: number } = {}
  ): number | null => {
    const options = {
      decimals: null, // Number of decimals
      ...opts,
    };

    const processedCost = parseFloat(String(cost));
    const processedPrice = parseFloat(String(price));

    if (!processedCost || !processedPrice) {
      return null;
    }

    let gpm = 1 - processedCost / processedPrice;

    if (options.decimals && options.decimals > 0) {
      gpm = NumberHelper.round(gpm, options.decimals);
    }

    return gpm;
  },

  computeAndFormatGpm: (
    cost: string | number,
    price: string | number,
    decimals: number
  ): string | null => {
    const gpm = PricingHelper.computeGpm(cost, price, { decimals });

    if (null === gpm) {
      return null;
    }

    return getPercentage(gpm);
  },

  computeGpmPercentage: (
    cost: string | number,
    price: string | number,
    decimals = 2
  ): number | null => {
    const gpm = PricingHelper.computeGpm(cost, price);

    if (null === gpm) {
      return null;
    }

    return NumberHelper.round(gpm * 100, decimals);
  },

  computePrice: (cost: number, gpm: number, allowZeroGpm = true): number | string | null => {
    if (!cost || 1 === +gpm) {
      return null;
    }

    if (!allowZeroGpm && 0 === +gpm) {
      return null;
    }

    return cost / (1 - gpm);
  },

  computeAndFormatPrice: (
    cost: number,
    gpm: number,
    options: { allowZeroGpm?: boolean; decimals?: number } = {}
  ): number | string | null => {
    const { allowZeroGpm = true, decimals } = options;
    const price = PricingHelper.computePrice(cost, gpm, allowZeroGpm);

    if (null === price) {
      return null;
    }

    return NumberHelper.round(price, decimals);
  },

  discount: (value: string, displayZero = true) => {
    const prefix = '-';

    if (!value || (!displayZero && 0 === Number(value))) {
      return '-';
    }

    return `${prefix}${getPercentage(Number(value) / 100)}`;
  },

  compare: (firstPrice: string, secondPrice: string): number | null =>
    NumberHelper.compare(firstPrice, secondPrice, PricingHelper.PRECISION),

  compareGpm: (firstGpm: string, secondGpm: string): number | null =>
    NumberHelper.compare(firstGpm, secondGpm, PricingHelper.GPM_PRECISION),

  isEmpty: (pricingValue: number): boolean => !(pricingValue > 0),

  isNonEmpty: (pricingValue: number): boolean => pricingValue > 0,

  getCurrencyFormat: (currency: string | boolean) => {
    if (currency === false) {
      return '';
    }

    return currency;
  },

  getDecimalsToDisplay: (
    amount: number,
    decimals: number,
    smallDecimals: number,
    bigDecimals: number
  ) => {
    if (amount < 1) {
      return smallDecimals;
    }

    if (decimals === undefined || decimals === null) {
      return amount >= 10 ? bigDecimals : PricingHelper.DEFAULT_PRICING_DECIMALS;
    }

    return decimals;
  },

  formatPricing: ({
    amount,
    format = null,
    currency = PriceCurrency.USD,
    thousandSeparator = true,
    decimals,
    smallDecimals = PricingHelper.DEFAULT_SMALL_PRICING_DECIMALS,
    bigDecimals = PricingHelper.DEFAULT_BIG_PRICING_DECIMALS,
    displayZero = true,
    fixedDecimals = false,
  }: {
    amount: number | null | undefined;
    format?: string;
    currency?: string;
    thousandSeparator?: boolean;
    decimals?: number;
    smallDecimals?: number;
    bigDecimals?: number;
    displayZero?: boolean;
    fixedDecimals?: boolean;
  }): string => {
    if (
      amount === null ||
      amount === undefined ||
      (!displayZero && (!amount || PricingHelper.isEmpty(amount)))
    ) {
      return DEFAULT_PLACEHOLDER;
    }

    return numeral(amount).format(
      `${PricingHelper.getCurrencyFormat(currency)}${NumberHelper.getFormat(
        thousandSeparator,
        fixedDecimals
          ? decimals
          : PricingHelper.getDecimalsToDisplay(
              Number(amount),
              decimals,
              smallDecimals,
              bigDecimals
            ),
        format,
        fixedDecimals
      )}`
    );
  },

  isCostValidByStatus: (status: CorporatePartCostStatus) =>
    ![CorporatePartCostStatus.OLD, CorporatePartCostStatus.EXPIRED].includes(status),

  formatPricingPerUom: (
    amount: number,
    uom: string,
    uomQuantity: number,
    format = null,
    currency = PriceCurrency.USD,
    decimals: number
  ) => {
    if (!(amount > 0)) {
      return DEFAULT_PLACEHOLDER;
    }

    const price = PricingHelper.formatPricing({
      amount,
      format,
      currency,
      decimals,
    });
    const perUom = NumberHelper.formatPerUom(uom, uomQuantity);

    return `${price} ${perUom}`;
  },

  computeAndFormatMultiplier: (purchaseCost?: number, standardCost?: number): string | null => {
    if (!purchaseCost || !standardCost) {
      return null;
    }

    return getPercentage(standardCost / purchaseCost - 1);
  },

  deriveSpaStandardCost: (
    externalPricing?: {
      externalSpaCost?: number | null;
      purchaseCost?: number | null;
      externalCost?: number | null;
    } | null
  ): number | null => {
    if (!externalPricing?.externalSpaCost) {
      return null;
    }
    if (externalPricing.externalSpaCost !== externalPricing.purchaseCost) {
      return null;
    }

    return externalPricing.externalCost ?? null;
  },
};

export default PricingHelper;
